/**
 * Vendor Jobs Hook
 * 
 * Fetches and filters jobs that have been awarded to a vendor organization.
 * This hook implements role-based access control, showing different job
 * sets based on the user's role within their vendor organization.
 * 
 * **Role-Based Access:**
 * - **Member**: Only jobs where they personally submitted the winning bid
 * - **Super User**: All jobs won by any member of the organization
 * - **Owner**: All jobs won by any member of the organization
 * 
 * **Job Lifecycle Tracking:**
 * - awarded: Job awarded, PO pending
 * - in_progress: Work actively being performed
 * - completed_pending_review: Vendor marked complete, awaiting client review
 * - accepted: Client accepted completion
 * - rejected: Client rejected completion, needs rework
 * - disputed: Disagreement requiring admin mediation
 * - closed: Final state, all actions complete
 * 
 * **Included Data:**
 * - Complete job details
 * - Award information (amount, selections)
 * - Purchase order status
 * - Bid details from winning bid
 * 
 * **Filtering Options:**
 * - Search by job title or description
 * - Filter by job status (single or multiple)
 * - Enable/disable query execution
 * 
 * **Use Cases:**
 * - Vendor dashboard job listings
 * - Active work tracking
 * - Job completion workflows
 * - Performance reporting
 * - Invoice generation
 * 
 * @param {UseVendorJobsOptions} options - Configuration options
 * @param {string} options.search - Search term for title/description
 * @param {string[]} options.status - Array of status values to filter by
 * @param {boolean} options.enabled - Whether to execute the query
 * 
 * @returns {Object} Vendor jobs state
 * @returns {Job[]} jobs - Array of awarded jobs
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if fetch failed
 * @returns {Function} refetch - Manually trigger data refresh
 * 
 * @example
 * // Fetch all active jobs for vendor
 * const { jobs, loading, error } = useVendorJobs({
 *   status: ['in_progress', 'completed_pending_review']
 * });
 * 
 * @example
 * // Search vendor's jobs
 * const { jobs } = useVendorJobs({
 *   search: searchTerm,
 *   enabled: searchTerm.length > 0
 * });
 * 
 * @example
 * // Vendor dashboard with refetch
 * const { jobs, loading, refetch } = useVendorJobs();
 * 
 * useEffect(() => {
 *   const interval = setInterval(refetch, 60000); // Refresh every minute
 *   return () => clearInterval(interval);
 * }, []);
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Job } from '@/lib/types/job';

interface UseVendorJobsOptions {
  search?: string;
  status?: string[];
  enabled?: boolean;
}

export const useVendorJobs = (options: UseVendorJobsOptions = {}) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVendorJobs = async () => {
    try {
      if (options.enabled === false) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      const user = await supabase.auth.getUser();
      if (!user.data.user) {
        throw new Error('Not authenticated');
      }

      // Get user's profile to check org_role via RPC
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.data.user.id)
        .single();

      if (!profile) {
        throw new Error('Profile not found');
      }

      // Fetch org role using RPC function
      const { data: orgRole } = await supabase
        .rpc('get_user_org_role', {
          _user_id: user.data.user.id,
          _org_id: profile.organization_id
        } as any);

      // Fetch jobs where vendor has won an award
      // Members: only jobs where they submitted the winning bid
      // Owners/Super Users: all jobs won by any org member
      let query = supabase
        .from('jobs')
        .select(`
          *,
          awards!inner(
            id,
            winning_bid_id,
            total_award_amount_cents,
            award_selections(
              total_price_cents
            ),
            bids!inner(
              vendor_user_id
            )
          ),
          purchase_order:purchase_orders!purchase_orders_job_id_fkey(
            id,
            po_amount_cents,
            status,
            updated_at
          )
        `)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });

      // Apply role-based filtering
      if (orgRole === 'member') {
        // Members can only see jobs they won
        query = query.eq('awards.bids.vendor_user_id', user.data.user.id);
      } else if (orgRole === 'owner' || orgRole === 'super_user') {
        // Owners and Super Users can see all jobs won by any org member
        const { data: orgUsers } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('organization_id', profile.organization_id);
        
        if (orgUsers && orgUsers.length > 0) {
          const userIds = orgUsers.map(u => u.user_id);
          query = query.in('awards.bids.vendor_user_id', userIds);
        }
      }

      // Apply search filter
      if (options.search) {
        query = query.or(`title.ilike.%${options.search}%,description.ilike.%${options.search}%`);
      }

      // Apply status filter - default to active work statuses if not specified
      const statusFilter = options.status && options.status.length > 0 
        ? options.status 
        : ['awarded', 'in_progress', 'completed_pending_review', 'accepted', 'rejected', 'disputed', 'closed'];
      
      query = query.in('status', statusFilter);

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      setJobs((data as unknown as Job[]) || []);
    } catch (err) {
      console.error('Error fetching vendor jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (options.enabled === false) {
      setLoading(false);
      return;
    }
    fetchVendorJobs();
  }, [options.enabled, options.search, JSON.stringify(options.status)]);

  const refetch = () => {
    fetchVendorJobs();
  };

  return {
    jobs,
    loading,
    error,
    refetch,
  };
};